<script lang="ts" setup>
import { Head, Link, useForm, usePage } from "@inertiajs/vue3";

import HeadingComponent from "../../Components/HeadingComponent.vue";
import TrackPublicWrapper from "../TrackPublic/TrackPublicWrapper.vue";
import { onMounted, onUnmounted, ref, computed } from "vue";
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";
import { PlusIcon, EyeIcon, EyeSlashIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";
import SpinnerIcon from "@/Components/assets/SpinnerIcon.vue";

momentDurationFormatSetup(moment);

import "/node_modules/flag-icons/css/flag-icons.min.css";
import ReplayIcon from "@/Components/assets/ReplayIcon.vue";
import TooltipIcon from "@/Components/TooltipIcon.vue";
import CcsPenaltiesTooltipContent from "@/Pages/EventPublic/Components/CcsPenaltiesTooltipContent.vue";
import EditIcon from "@/Components/assets/EditIcon.vue";
import CompetitorClassSectionPenaltiesFormModal from "@/Pages/EventPublic/Components/CompetitorClassSectionPenaltiesFormModal.vue";
import eventBus from "@/Helpers/eventBus";
import { DateTime, Duration } from "luxon";
import FwbTooltip from "@/Components/FwbTooltip/FwbTooltip.vue";
import MyVehicleModal from "@/Components/MyVehicleModal.vue";
import Button from "@/Components/Button.vue";
import { convertKmAsPerTrackUnits, formatTrackDistanceHuman } from "../../Helpers/helperFunctions";
import DialogModal from "@/Components/DialogModal.vue";
import JetButton from "@/Components/PrimaryButton.vue";

interface Props {
    track: Track;
    canBuyTrack: boolean;
    track_results: object;
}

const startListClassSelected = ref(null);
const startListSectionSelected = ref(null);
const modalNameVisible = ref(null);
const modalNameVisibleForm = useForm({
    data: null,
});

// Sorting state
const sortField = ref<string | null>(null);
const sortDirection = ref<'asc' | 'desc'>('asc');

// Sorting function
function sortBy(field: string) {
    if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortField.value = field;
        sortDirection.value = 'asc';
    }
}

// Computed property for sorted track results
const sortedTrackResults = computed(() => {
    if (!sortField.value) {
        return usePage().props.track_results;
    }

    const results = [...usePage().props.track_results];
    
    return results.sort((a: any, b: any) => {
        let aValue: any, bValue: any;
        
        switch (sortField.value) {
            case 'position':
                aValue = a.pos || 999999;
                bValue = b.pos || 999999;
                break;
            case 'total_time':
                aValue = a.total_time ? moment.duration(a.total_time, 'milliseconds').asMilliseconds() : 999999999;
                bValue = b.total_time ? moment.duration(b.total_time, 'milliseconds').asMilliseconds() : 999999999;
                break;
            case 'bonif_penal':
                aValue = (a.total_penalties_s || 0) + (a.bonus_s || 0);
                bValue = (b.total_penalties_s || 0) + (b.bonus_s || 0);
                break;
            case 'time_ridden':
                aValue = a.time ? moment.duration(a.time, 'milliseconds').asMilliseconds() : 999999999;
                bValue = b.time ? moment.duration(b.time, 'milliseconds').asMilliseconds() : 999999999;
                break;
            case 'distance':
                aValue = a.meters || 0;
                bValue = b.meters || 0;
                break;
            case 'dss_start':
                aValue = a.start_at ? new Date(a.start_at).getTime() : 999999999999;
                bValue = b.start_at ? new Date(b.start_at).getTime() : 999999999999;
                break;
            case 'track_version':
                aValue = a.classSection?.trackVersion?.version || 0;
                bValue = b.classSection?.trackVersion?.version || 0;
                break;
            default:
                return 0;
        }
        
        if (sortDirection.value === 'asc') {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
    });
});

function modalNameVisibleFormSubmit() {
    modalNameVisibleForm.post(route(`toggle-ccs-visibility`, { ccs: modalNameVisible.value.id }), {
        preserveScroll: true,
        onSuccess: (response) => {
            // console.log(response);
            modalNameVisible.value = null;
        },
    });
}

const props = withDefaults(defineProps<Props>(), {});

function showCcsPenaltiesFormModal(ccs) {
    eventBus.$emit("showCcsPenaltiesFormModal", {
        ccs: ccs,
        useAxios: false,
        trackUnits: props.track.units,
    });
}

function getSections(eventClass, startListSectionSelected) {
    return startListSectionSelected !== null
        ? eventClass.classSections.filter((cs) => cs.id === startListSectionSelected)
        : eventClass.classSections;
}

function isDNInThisSection(classSection) {
    return classSection.waypoints.find((csc) => {
        return csc.types.includes("DN");
    });
}

function filterClassSectionWaypoints(classSection) {
    if (!classSection.filter_only_dn) {
        return classSection.waypoints;
    }

    let ccw = classSection.waypoints;

    ccw = ccw.filter((csw) => {
        // console.log(c.event_class_id);
        return csw.types.includes("DSS") || csw.types.includes("ASS") || csw.types.includes("DN");
    });

    return ccw;
}

function findCompetitorClassSectionWaypoint(competitorClassSection, wpt, field = "in_time") {
    const foundCompetitorWpt = competitorClassSection.class_section_instructions.find((csInstruction) => {
        if (csInstruction.id === wpt.id) {
            return csInstruction;
        }
    });

    if (foundCompetitorWpt) {
        if (wpt.types.includes("DSS")) {
            if (field === "in_time" || field === "out_time") {
                return "00:00:00";
            }
            return DateTime.fromISO(competitorClassSection.start_at)
                .setZone(usePage().props.track.timezone)
                .toLocaleString(DateTime.TIME_24_WITH_SECONDS);
        }

        if (field === "in_time" || field === "out_time") {
            return moment
                .duration(foundCompetitorWpt.pivot[field], "milliseconds")
                .format("hh:mm:ss", { trim: false });
        }

        // console.log(foundCompetitorWpt.pivot[field]);

        const clockTime = new Date(foundCompetitorWpt.pivot[field]);
        const ms = clockTime.getMilliseconds();
        // round seconds up if needed
        if (ms >= 500) {
            clockTime.setSeconds(clockTime.getSeconds() + 1);
        }

        return DateTime.fromJSDate(clockTime)
            .setZone(usePage().props.track.timezone)
            .toLocaleString(DateTime.TIME_24_WITH_SECONDS);
    }

    return "-";
}

function getOverallCompetitors(event_class_id) {
    let ccc = usePage()
        .props.competitors.filter((c) => {
            // console.log(c.event_class_id);
            return event_class_id ? c.event_class_id === event_class_id : true;
        })
        .map((c) => {
            Object.assign(c, getCompetitorOverallTime(c));
            // console.log(c);
            return c;
        })
        .filter((c) => c.overall_duration_s && !c.overall_state)
        .sort(function compareFn(a, b) {
            if (props.track.id != 67) {
                // TODO: Alakonnu ir problēma ar šo...
                if (a.overall_section_count < b.overall_section_count) {
                    return 1;
                }
                if (a.overall_section_count > b.overall_section_count) {
                    return -1;
                }
            }

            if (a.overall_duration_s < b.overall_duration_s) {
                return -1;
            }
            if (a.overall_duration_s > b.overall_duration_s) {
                return 1;
            }
            // a must be equal to b
            return 0;
        });

    let currentMaxSectionsCount = 0;
    if (ccc.length) {
        currentMaxSectionsCount = ccc[0].overall_section_count;
    }

    // rādam tikai tos, kas nobraukuši visvairāk sekcijas, un vienādu to skaitu.
    // tādā veidā nerādam tos, kas vēl nav nobraukuši vienādu skaitu sekciju
    if (props.track.id != 67) {
        // TODO: Alakonnu ir problēma ar šo...
        ccc = ccc.filter((c) => c.overall_section_count === currentMaxSectionsCount);
    }

    return ccc;
}

function getCompetitorOverallTime(competitor) {
    let duration = null;
    let bonus_s = 0;
    let total_penalties_s = 0;
    let state = null;
    let overall_section_count = 0;
    for (let i2 = 0, k2 = Object.keys(props.track_results.classes), l2 = k2.length; i2 < l2; i2++) {
        const eventClass = props.track_results.classes[k2[i2]];

        for (let i = 0, l = eventClass.classSections.length; i < l; i++) {
            const cs = eventClass.classSections[i];

            const css = cs.competitorClassSections.find((c) => c.competitor_id === competitor.id);
            if (css && css.state) {
                state = css.state;
            }
            if (css && css.time && css.total_time) {
                overall_section_count++;
                if (!cs.is_add_to_overall_results) {
                    // šis bloks ir te, lai paspēj pieskaitīt overall_section_count
                    // ja vienai klasei prologu skaita klāt, bet otrai nē, tad ir problēma.
                    // tpc vjag pieskaitīt overall_section_count arī sekcijām, kuras neskaita kopvērtējumā.
                    // tas vajadzīgs, lai visu klašu overall korekti darbotos.
                    continue;
                }
                if (!duration) {
                    duration = moment.duration(css.total_time, "milliseconds");
                } else {
                    duration = duration.add(moment.duration(css.total_time, "milliseconds"));
                }

                if (css.bonus_s) {
                    bonus_s += css.bonus_s;
                }

                if (css.total_penalties_s) {
                    total_penalties_s += css.total_penalties_s;
                }
            }
        }
    }

    if (!duration) {
        return {};
    }

    return {
        overall_duration_s: Math.round(duration.as("seconds")),
        overall_duration: duration.format("hh:mm:ss", { trim: false }),
        overall_bonus_s: bonus_s,
        overall_total_penalties_s: total_penalties_s,
        overall_state: state,
        overall_section_count: overall_section_count,
    };
}

function shouldShowCcsPenaltiesTooltip(ccs) {
    return ccs.bonus_s > 0 || ccs.penalty_s > 0 || ccs.penalties.length > 0;
}

function showMyVehicleModal(ccs) {
    eventBus.$emit("showMyVehicleModal", {
        type: "leaderboard",
        ccs: ccs,
    });
}

function getSplitTime(ccsIndex, ccs, firstOrPrev) {
    let fromTime;
    if (firstOrPrev === "first") {
        fromTime = usePage().props.track_results[0].total_time;
    }
    if (firstOrPrev === "prev") {
        fromTime = usePage().props.track_results[ccsIndex - 1].total_time;
    }

    // splittojam no punkta tpc, ka time ir milisekundēs.
    // Pēc idejas var noapaļot rezultātus līdz apalīa sekundei un tad šo ņemt nost
    fromTime = fromTime.split(".")[0];
    let toTime = ccs.total_time.split(".")[0];

    fromTime = [...fromTime.split(":")].map((el) => parseInt(el)).reverse();
    const fromTimeObject = {};
    if (fromTime.length === 1) {
        fromTimeObject.seconds = fromTime[0];
    }
    if (fromTime.length === 2) {
        fromTimeObject.seconds = fromTime[0];
        fromTimeObject.minutes = fromTime[1];
    }
    if (fromTime.length === 3) {
        fromTimeObject.seconds = fromTime[0];
        fromTimeObject.minutes = fromTime[1];
        fromTimeObject.hours = fromTime[2];
    }

    toTime = [...toTime.split(":")].map((el) => parseInt(el)).reverse();
    const toTimeObject = {};
    if (toTime.length === 1) {
        toTimeObject.seconds = toTime[0];
    }
    if (toTime.length === 2) {
        toTimeObject.seconds = toTime[0];
        toTimeObject.minutes = toTime[1];
    }
    if (toTime.length === 3) {
        toTimeObject.seconds = toTime[0];
        toTimeObject.minutes = toTime[1];
        toTimeObject.hours = toTime[2];
    }
    const fromDuration = Duration.fromObject(fromTimeObject).as("seconds");
    const toDuration = Duration.fromObject(toTimeObject).as("seconds");

    const diffInSeconds = toDuration - fromDuration;
    const diffDuration = Duration.fromObject({ seconds: diffInSeconds });

    // const diff = DateTime.fromSQL(toTime).diff(DateTime.fromSQL(fromTime), [
    //     "days",
    //     "hours",
    //     "minutes",
    //     "seconds",
    // ]);

    // griežam nost prikšējās nulles
    const formatStr = [];
    if (diffDuration.as("hours") > 1) {
        formatStr.push("hh");
    }
    if (diffDuration.as("minutes") > 1) {
        formatStr.push("mm");
    }
    if (diffDuration.as("seconds") > 1) {
        formatStr.push("ss");
    }

    let formattedDiff = diffDuration.toFormat(formatStr.join(":"));

    if (formattedDiff[0] == "0") {
        formattedDiff = formattedDiff.substring(1, formattedDiff.length);
    }
    return formattedDiff;
}

onMounted(() => {});

onUnmounted(() => {});
</script>

<template>
    <TrackPublicWrapper>
        <Head>
            <title>Track leaderboard: {{ $page.props.track.name }}</title>
            <meta
                property="og:title"
                head-key="og:title"
                :content="`Track leaderboard: ${$page.props.meta_track_name}`"
            />
        </Head>
        <div class="flex-1 space-y-4 overflow-auto md:overflow-visible">
            <div
                v-if="track.event_id"
                class="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-blue-800"
                role="alert"
            >
                <div class="flex items-center">
                    <svg
                        class="me-2 size-4 shrink-0"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                        />
                    </svg>
                    <span class="sr-only">Info</span>
                    <h3 class="text-lg font-medium">This Track belongs to the Event</h3>
                </div>
                <div class="mt-2 text-sm">
                    <Link
                        :href="
                            route('event.public.information', {
                                hashid: track.event.hashid,
                                seo_slug: track.event.seo_slug,
                            })
                        "
                    >
                        {{ track.event.name }}
                    </Link>
                </div>
            </div>

            <div class="max-w-7xl space-y-4">
                <HeadingComponent class="rounded-lg bg-white p-4 shadow-lg sm:px-6">
                    <template #name>Track Leaderboard</template>
                    <template #description>
                        {{ $page.props.track.name }}
                        <span v-if="track.latestVersion.distance > 0"
                            >({{ formatTrackDistanceHuman(track) }})</span
                        ></template
                    >
                </HeadingComponent>
                <div
                    v-if="!track.latestVersion.is_valid"
                    class="mb-4 flex rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800"
                    role="alert"
                >
                    <svg
                        class="me-3 mt-[2px] inline size-4 shrink-0"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                        />
                    </svg>
                    <div>
                        <span class="font-medium">Invalid track structure</span>
                        <div class="mt-2 text-sm">
                            Calculations of results and penalties may be wrong. Please fix your track structure
                            errors!
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="$page.props.track_results.length === 0"
                class="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-blue-800"
                role="alert"
            >
                <div class="flex items-center">
                    <svg
                        class="me-2 size-4 shrink-0"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                        />
                    </svg>
                    <span class="sr-only">Info</span>
                    <h3 class="text-lg font-medium">
                        No data to display yet for this track's leaderboard. You can be the first to ride it!
                    </h3>
                </div>
            </div>
            <div
                v-else
                class="flex w-full flex-col space-y-4 overflow-auto md:w-auto"
            >
                <div class="flex w-full flex-col overflow-auto md:w-auto">
                    <hr class="border-8 border-gray-300" />
                    <h2 class="p-4 text-left text-2xl">All rides ({{ $page.props.track_results.length }}):</h2>
                    <hr class="border-2 border-gray-300" />
                    <div class="max-w-full overflow-auto rounded border border-gray-200 bg-white shadow">
                        <table class="table-auto tabular-nums">
                            <thead>
                                <tr>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        &nbsp;
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        <FwbTooltip>
                                            <template #trigger> Ride <br />No.</template>
                                            <template #content>
                                                Which ride attempt is this in a row for this rider?
                                            </template>
                                        </FwbTooltip>
                                    </th>
                                    <th
                                        scope="col"
                                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer hover:bg-gray-50"
                                        @click="sortBy('position')"
                                    >
                                        <div class="flex items-center space-x-1">
                                            <FwbTooltip>
                                                <template #trigger> P.</template>
                                                <template #content> Position</template>
                                            </FwbTooltip>
                                            <div v-if="sortField === 'position'" class="flex flex-col">
                                                <ChevronUpIcon 
                                                    class="w-3 h-3" 
                                                    :class="sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                                <ChevronDownIcon 
                                                    class="w-3 h-3 -mt-1" 
                                                    :class="sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        &nbsp;
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        Rider
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-50"
                                        @click="sortBy('total_time')"
                                    >
                                        <div class="flex items-center space-x-1">
                                            <span>Total time</span>
                                            <div v-if="sortField === 'total_time'" class="flex flex-col">
                                                <ChevronUpIcon 
                                                    class="w-3 h-3" 
                                                    :class="sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                                <ChevronDownIcon 
                                                    class="w-3 h-3 -mt-1" 
                                                    :class="sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        First <br />
                                        Prev.
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-50"
                                        @click="sortBy('bonif_penal')"
                                    >
                                        <div class="flex items-center space-x-1">
                                            <span>Bonif./Penal.</span>
                                            <div v-if="sortField === 'bonif_penal'" class="flex flex-col">
                                                <ChevronUpIcon 
                                                    class="w-3 h-3" 
                                                    :class="sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                                <ChevronDownIcon 
                                                    class="w-3 h-3 -mt-1" 
                                                    :class="sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-50"
                                        @click="sortBy('time_ridden')"
                                    >
                                        <div class="flex items-center space-x-1">
                                            <FwbTooltip>
                                                <template #trigger> Time ridden</template>
                                                <template #content>
                                                    Total riding time minus neutralization periods and time spent in
                                                    transfer zones
                                                </template>
                                            </FwbTooltip>
                                            <div v-if="sortField === 'time_ridden'" class="flex flex-col">
                                                <ChevronUpIcon 
                                                    class="w-3 h-3" 
                                                    :class="sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                                <ChevronDownIcon 
                                                    class="w-3 h-3 -mt-1" 
                                                    :class="sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-50"
                                        @click="sortBy('distance')"
                                    >
                                        <div class="flex items-center space-x-1">
                                            <FwbTooltip>
                                                <template #trigger>
                                                    *Distance <br />
                                                    <span class="whitespace-nowrap">{{
                                                        formatTrackDistanceHuman(track, 1)
                                                    }}</span></template
                                                >
                                                <template #content>
                                                    Distance ridden (Alpha feature, could be wrong in some
                                                    cases)</template
                                                >
                                            </FwbTooltip>
                                            <div v-if="sortField === 'distance'" class="flex flex-col">
                                                <ChevronUpIcon 
                                                    class="w-3 h-3" 
                                                    :class="sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                                <ChevronDownIcon 
                                                    class="w-3 h-3 -mt-1" 
                                                    :class="sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer hover:bg-gray-50"
                                        @click="sortBy('dss_start')"
                                    >
                                        <div class="flex items-center space-x-1">
                                            <span>DSS (start)</span>
                                            <div v-if="sortField === 'dss_start'" class="flex flex-col">
                                                <ChevronUpIcon 
                                                    class="w-3 h-3" 
                                                    :class="sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                                <ChevronDownIcon 
                                                    class="w-3 h-3 -mt-1" 
                                                    :class="sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                    >
                                        ASS (finish)
                                    </th>
                                    <th
                                        scope="col"
                                        class="border-l border-dotted px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-50"
                                        @click="sortBy('track_version')"
                                    >
                                        <div class="flex items-center space-x-1">
                                            <FwbTooltip>
                                                <template #trigger> Track version</template>
                                                <template #content>
                                                    Occasionally, tracks may require updates, usually minor ones.
                                                    You can compare rides for each track version. Replays are
                                                    available for all versions of the track that have been ridden.
                                                </template>
                                            </FwbTooltip>
                                            <div v-if="sortField === 'track_version'" class="flex flex-col">
                                                <ChevronUpIcon 
                                                    class="w-3 h-3" 
                                                    :class="sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                                <ChevronDownIcon 
                                                    class="w-3 h-3 -mt-1" 
                                                    :class="sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'" 
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        Weight (kg)
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        Engine (cc)
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        Cyl.
                                    </th>
                                    <th
                                        v-if="track.navigate_by === 'rb'"
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        Autoscroll
                                    </th>
                                    <th
                                        v-if="track.navigate_by === 'rb'"
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        Roadbook <br />
                                        style
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        Age
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                        Gender
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white">
                                <tr
                                    v-for="(competitorClassSection, ccsIndex) in sortedTrackResults"
                                    :key="'ccs' + competitorClassSection.id"
                                    class="group/ccs-toggle"
                                    :class="[
                                        ccsIndex === 0 ? 'border-gray-300' : 'border-gray-200',
                                        'border-t',
                                        competitorClassSection.ride_no > 1 ? 'bg-gray-50' : '',
                                        $page.props.competitor &&
                                        $page.props.competitor?.id === competitorClassSection.competitor_id
                                            ? 'font-bold'
                                            : '',
                                    ]"
                                >
                                    <td class="whitespace-nowrap pl-3">
                                        <Link
                                            :href="
                                                route('track.public.replay', {
                                                    pin: track.pin,
                                                    seo_slug: track.seo_slug,
                                                }) +
                                                '#ccs=|' +
                                                competitorClassSection.id
                                            "
                                        >
                                            <ReplayIcon class="size-6"></ReplayIcon>
                                        </Link>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-3 py-4 text-gray-500"
                                        :class="competitorClassSection.ride_no === 1 ? 'font-bold' : 'text-sm'"
                                    >
                                        #{{ competitorClassSection.ride_no }}
                                        <span
                                            v-if="
                                                competitorClassSection.competitor_id &&
                                                $page.props.competitor?.id ===
                                                    competitorClassSection.competitor_id
                                            "
                                            class="hidden group-hover/ccs-toggle:inline"
                                            ><br /><Button
                                                class="h-4"
                                                :class-name="'px-1'"
                                                variant="outline"
                                                @click="modalNameVisible = competitorClassSection"
                                                ><EyeIcon
                                                    v-if="competitorClassSection.is_masked"
                                                    class="size-4"
                                                /><EyeSlashIcon
                                                    v-if="!competitorClassSection.is_masked"
                                                    class="size-4"
                                                /> </Button
                                        ></span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-gray-500 sm:pl-6"
                                    >
                                        {{ competitorClassSection.pos }}
                                        <FwbTooltip v-if="!competitorClassSection.pos">
                                            <template #trigger>
                                                <SpinnerIcon class="mr-2 inline size-5"></SpinnerIcon>
                                            </template>
                                            <template #content> Calculating...</template>
                                        </FwbTooltip>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        :class="competitorClassSection.is_masked ? 'blur-sm' : ''"
                                    >
                                        <span
                                            v-if="!competitorClassSection.is_masked"
                                            :title="
                                                $page.props.app.countries[
                                                    competitorClassSection.competitor.user.country.toLowerCase()
                                                ].name
                                            "
                                            :class="`border text-xl fi fi-${competitorClassSection.competitor.user.country.toLowerCase()}`"
                                        ></span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        :class="{
                                            'blur-sm': competitorClassSection.is_masked,
                                        }"
                                    >
                                        <template v-if="!competitorClassSection.is_masked">
                                            <span>
                                                {{ competitorClassSection.competitor.driver_full_name }}
                                            </span>
                                            <br />
                                            <template
                                                v-if="
                                                    $page.props.competitor?.id ===
                                                    competitorClassSection.competitor_id
                                                "
                                            >
                                                <span
                                                    v-if="competitorClassSection.myvehicle?.id"
                                                    class="cursor-pointer text-blue-600 visited:text-violet-500 hover:underline"
                                                    @click="showMyVehicleModal(competitorClassSection)"
                                                >
                                                    {{ competitorClassSection.myvehicle?.make }} ({{
                                                        competitorClassSection.myvehicle?.model
                                                    }})
                                                </span>
                                                <span
                                                    v-else
                                                    class="cursor-pointer font-bold text-red-500"
                                                    @click="showMyVehicleModal(competitorClassSection)"
                                                >
                                                    <Button
                                                        class="h-4"
                                                        variant="outline"
                                                        @click="showMyVehicleModal(competitorClassSection)"
                                                        ><PlusIcon
                                                            class="-ml-0.5 mr-1.5 size-4"
                                                            aria-hidden="true"
                                                        />
                                                        Add vehicle</Button
                                                    >
                                                </span>
                                            </template>
                                            <template v-else
                                                >{{ competitorClassSection.myvehicle?.make }}
                                                <span v-if="competitorClassSection.myvehicle?.model"
                                                    >({{ competitorClassSection.myvehicle?.model }})</span
                                                >
                                            </template>
                                        </template>
                                        <template v-else>
                                            <FwbTooltip>
                                                <template #trigger> Masked Name</template>
                                                <template #content> Rider has masked this ride</template>
                                            </FwbTooltip>
                                        </template>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-4 tabular-nums">
                                        <template v-if="competitorClassSection.total_time">
                                            <FwbTooltip>
                                                <template #trigger>
                                                    {{
                                                        moment
                                                            .duration(
                                                                competitorClassSection.total_time,
                                                                "milliseconds",
                                                            )
                                                            .format("hh:mm:ss", {
                                                                trim: false,
                                                            })
                                                    }}
                                                </template>
                                                <template #content>
                                                    {{
                                                        moment
                                                            .duration(
                                                                competitorClassSection.total_time,
                                                                "milliseconds",
                                                            )
                                                            .format("hh:mm:ss.SSS", {
                                                                trim: false,
                                                            })
                                                    }}
                                                </template>
                                            </FwbTooltip>
                                        </template>
                                        <FwbTooltip v-if="competitorClassSection.is_riding">
                                            <template #trigger>
                                                <SpinnerIcon class="mr-2 inline size-5"></SpinnerIcon>
                                            </template>
                                            <template #content> Riding...</template>
                                        </FwbTooltip>
                                    </td>
                                    <td class="text-right text-sm tabular-nums text-gray-500">
                                        <template
                                            v-if="
                                                competitorClassSection.ride_no === 1 &&
                                                ccsIndex > 0 &&
                                                competitorClassSection.total_time
                                            "
                                        >
                                            +{{ getSplitTime(ccsIndex, competitorClassSection, "first") }}
                                            <br />+{{ getSplitTime(ccsIndex, competitorClassSection, "prev") }}
                                        </template>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        <div
                                            v-if="competitorClassSection.bonus_s !== 0"
                                            class="text-green-500"
                                        >
                                            +{{
                                                moment
                                                    .duration(
                                                        Math.abs(competitorClassSection.bonus_s),
                                                        "seconds",
                                                    )
                                                    .format("hh:mm:ss", {
                                                        trim: false,
                                                    })
                                            }}
                                        </div>
                                        <div
                                            v-if="
                                                competitorClassSection.total_penalties_s +
                                                    competitorClassSection.bonus_s !==
                                                0
                                            "
                                            class="text-red-500"
                                        >
                                            -{{
                                                moment
                                                    .duration(
                                                        Math.abs(
                                                            competitorClassSection.total_penalties_s +
                                                                competitorClassSection.bonus_s,
                                                        ),
                                                        "seconds",
                                                    )
                                                    .format("hh:mm:ss", {
                                                        trim: false,
                                                    })
                                            }}
                                        </div>
                                        <div class="relative flex sm:static">
                                            <TooltipIcon
                                                v-if="shouldShowCcsPenaltiesTooltip(competitorClassSection)"
                                                class="leading-2 inline-block text-black"
                                            >
                                                <ccs-penalties-tooltip-content
                                                    :ccs="competitorClassSection"
                                                    :penalty_types="$page.props.penalty_types"
                                                    :competitor_states="$page.props.competitor_states"
                                                ></ccs-penalties-tooltip-content>
                                            </TooltipIcon>
                                            <span
                                                v-if="$page.props.can.update_track"
                                                class="px-3 text-sm"
                                            >
                                                <button
                                                    class="group cursor-pointer space-x-1 p-1 font-semibold md:p-0"
                                                    @click="showCcsPenaltiesFormModal(competitorClassSection)"
                                                >
                                                    <EditIcon class="size-5"></EditIcon>
                                                </button>
                                            </span>
                                        </div>

                                        <div v-if="competitorClassSection.state">
                                            {{ competitorClassSection.state }}
                                        </div>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-4">
                                        <FwbTooltip v-if="competitorClassSection.time">
                                            <template #trigger>
                                                {{
                                                    moment
                                                        .duration(competitorClassSection.time, "milliseconds")
                                                        .format("hh:mm:ss", {
                                                            trim: false,
                                                        })
                                                }}
                                            </template>
                                            <template #content>
                                                {{
                                                    moment
                                                        .duration(competitorClassSection.time, "milliseconds")
                                                        .format("hh:mm:ss.SSS", {
                                                            trim: false,
                                                        })
                                                }}
                                            </template>
                                        </FwbTooltip>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        {{
                                            convertKmAsPerTrackUnits(
                                                competitorClassSection.meters / 1000,
                                                $props.track.units,
                                                1,
                                            )
                                        }}
                                    </td>
                                    <td class="py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                                        <FwbTooltip v-if="competitorClassSection.start_at">
                                            <template #trigger>
                                                {{
                                                    DateTime.fromSeconds(
                                                        Math.round(
                                                            new Date(
                                                                competitorClassSection.start_at,
                                                            ).getTime() / 1000,
                                                        ),
                                                    )
                                                        .setZone($page.props.track.timezone)
                                                        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                                                }}
                                            </template>
                                            <template #content>
                                                {{
                                                    DateTime.fromISO(competitorClassSection.start_at)
                                                        .setZone($page.props.track.timezone)
                                                        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                                                }}.{{
                                                    DateTime.fromISO(competitorClassSection.start_at)
                                                        .setZone($page.props.track.timezone)
                                                        .toFormat("SSS")
                                                }}
                                            </template>
                                        </FwbTooltip>
                                    </td>
                                    <td class="py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                                        <FwbTooltip v-if="competitorClassSection.finish_at">
                                            <template #trigger>
                                                {{
                                                    DateTime.fromSeconds(
                                                        Math.round(
                                                            new Date(
                                                                competitorClassSection.finish_at,
                                                            ).getTime() / 1000,
                                                        ),
                                                    )
                                                        .setZone($page.props.track.timezone)
                                                        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                                                }}
                                            </template>
                                            <template #content>
                                                {{
                                                    DateTime.fromISO(competitorClassSection.finish_at)
                                                        .setZone($page.props.track.timezone)
                                                        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                                                }}.{{
                                                    DateTime.fromISO(competitorClassSection.finish_at)
                                                        .setZone($page.props.track.timezone)
                                                        .toFormat("SSS")
                                                }}
                                            </template>
                                        </FwbTooltip>
                                    </td>
                                    <td
                                        class="whitespace-nowrap border-l border-dotted py-4 pl-4 pr-3 text-sm sm:pl-6"
                                    >
                                        v.{{ competitorClassSection.classSection.trackVersion.version }}
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        :class="competitorClassSection.is_masked ? 'blur-sm' : ''"
                                    >
                                        <template
                                            v-if="
                                                !competitorClassSection.is_masked &&
                                                $page.props.competitor?.id ===
                                                    competitorClassSection.competitor_id
                                            "
                                        >
                                            <span
                                                v-if="competitorClassSection.myvehicle?.weight"
                                                class="cursor-pointer text-blue-600 visited:text-violet-500 hover:underline"
                                                @click="showMyVehicleModal(competitorClassSection)"
                                            >
                                                {{ competitorClassSection.myvehicle?.weight }}
                                            </span>
                                            <span
                                                v-else
                                                class="cursor-pointer font-bold text-red-500"
                                                @click="showMyVehicleModal(competitorClassSection)"
                                                >???</span
                                            >
                                        </template>
                                        <template v-else
                                            >{{ competitorClassSection.myvehicle?.weight }}
                                        </template>
                                        <template v-if="competitorClassSection.is_masked">Mask</template>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        :class="competitorClassSection.is_masked ? 'blur-sm' : ''"
                                    >
                                        <template
                                            v-if="
                                                !competitorClassSection.is_masked &&
                                                $page.props.competitor?.id ===
                                                    competitorClassSection.competitor_id
                                            "
                                        >
                                            <span
                                                v-if="competitorClassSection.myvehicle?.cc"
                                                class="cursor-pointer text-blue-600 visited:text-violet-500 hover:underline"
                                                @click="showMyVehicleModal(competitorClassSection)"
                                            >
                                                {{ competitorClassSection.myvehicle?.cc }}
                                            </span>
                                            <span
                                                v-else
                                                class="cursor-pointer font-bold text-red-500"
                                                @click="showMyVehicleModal(competitorClassSection)"
                                                >???</span
                                            >
                                        </template>
                                        <template v-else>{{ competitorClassSection.myvehicle?.cc }}</template>
                                        <template v-if="competitorClassSection.is_masked">Mask</template>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        :class="competitorClassSection.is_masked ? 'blur-sm' : ''"
                                    >
                                        <template
                                            v-if="
                                                !competitorClassSection.is_masked &&
                                                $page.props.competitor?.id ===
                                                    competitorClassSection.competitor_id
                                            "
                                        >
                                            <span
                                                v-if="competitorClassSection.myvehicle?.cylinders"
                                                class="cursor-pointer text-blue-600 visited:text-violet-500 hover:underline"
                                                @click="showMyVehicleModal(competitorClassSection)"
                                            >
                                                {{ competitorClassSection.myvehicle?.cylinders }}
                                            </span>
                                            <span
                                                v-else
                                                class="cursor-pointer font-bold text-red-500"
                                                @click="showMyVehicleModal(competitorClassSection)"
                                                >???</span
                                            >
                                        </template>
                                        <template v-else
                                            >{{ competitorClassSection.myvehicle?.cylinders }}
                                        </template>
                                        <template v-if="competitorClassSection.is_masked">---</template>
                                    </td>
                                    <td
                                        v-if="track.navigate_by === 'rb'"
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        :class="!competitorClassSection.is_used_autoscroll ? 'font-bold' : ''"
                                    >
                                        {{ competitorClassSection.is_used_autoscroll ? "yes" : "no" }}
                                    </td>
                                    <td
                                        v-if="track.navigate_by === 'rb'"
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                    >
                                        {{
                                            competitorClassSection.is_used_countdown_style
                                                ? "Countdown"
                                                : "Classic"
                                        }}
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        :class="competitorClassSection.is_masked ? 'blur-sm' : ''"
                                    >
                                        <span
                                            v-if="
                                                !competitorClassSection.is_masked &&
                                                competitorClassSection.competitor.user.date_of_birth &&
                                                Math.floor(
                                                    DateTime.fromISO(competitorClassSection.created_at)
                                                        .diff(
                                                            DateTime.fromISO(
                                                                competitorClassSection.competitor.user
                                                                    .date_of_birth,
                                                            ),
                                                            'years',
                                                        )
                                                        .toObject().years,
                                                ) > 0
                                            "
                                        >
                                            {{
                                                Math.floor(
                                                    DateTime.fromISO(competitorClassSection.created_at)
                                                        .diff(
                                                            DateTime.fromISO(
                                                                competitorClassSection.competitor.user
                                                                    .date_of_birth,
                                                            ),
                                                            "years",
                                                        )
                                                        .toObject().years,
                                                ).toFixed(0)
                                            }}
                                        </span>
                                        <template v-if="competitorClassSection.is_masked">---</template>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        :class="competitorClassSection.is_masked ? 'blur-sm' : ''"
                                    >
                                        <span v-if="!competitorClassSection.is_masked">{{
                                            competitorClassSection.competitor.user.gender
                                        }}</span>
                                        <template v-if="competitorClassSection.is_masked">---</template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="pt-4 text-sm">
                        <Link href="/faq/track-leaderboard-66"> FAQ: Penalty rules for Track Leaderboard</Link>
                        <br />
                        <span class="text-sm"
                            >Please note: As this is a BETA version of Track Leaderboards, all features and
                            rules are provisional and may be adjusted in response to
                            <a href="/contact-us">your input</a> and testing results.</span
                        >

                        <br />
                        <br />
                        <div class="text-xs">
                            *Reminder from the <a href="/terms-of-service">Terms of Service</a>: RoadbookRally
                            does not take responsibility for any track conditions and explicitly states that the
                            tracks are not designed for riding in competition mode. You have to follow traffic
                            and any other laws. RoadbookRally services and tracks are solely intended for
                            training and ride analysis purposes. Participants must use the tracks accordingly or
                            not use them at all, and acknowledge that RoadbookRally will not be liable for any
                            incidents or liabilities arising from any type of services, track and/or event
                            usage.
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <CompetitorClassSectionPenaltiesFormModal
            v-if="$page.props.can.update_track"
            :event="$page.props.track"
        ></CompetitorClassSectionPenaltiesFormModal>
    </TrackPublicWrapper>

    <MyVehicleModal></MyVehicleModal>

    <dialog-modal
        :show="modalNameVisible != null"
        @close="modalNameVisible = null"
    >
        <template #title>Control your ride privacy</template>

        <template #content>
            <div class="mt-4">You ride is {{ modalNameVisible.is_masked ? "masked" : "not masked" }}.</div>
            <div class="mt-4">
                For masked rides system will blur all your data, except: <br />
                <div class="text-sm">
                    - Ride number <br />
                    - Position <br />
                    - Total time <br />
                    - Bonif./Penal. <br />
                    - Time <br />
                    - Distance <br />
                    - DSS (start) time <br />
                    - ASS (finish) time <br />
                    - Track version <br />
                    - Autoscroll <br />
                    - Roadbook style <br />
                </div>
                <br />
                If you decide to mask your identification data on the Leaderboard, it will also be masked on the
                Replay page.
                <br />

                <br />This means that your ride details will not be visible to others (except the track owner),
                ensuring privacy for your activities on both the Leaderboard and the Replay page.
            </div>
        </template>

        <template #footer>
            <Button
                color="white"
                @click="modalNameVisible = null"
            >
                Cancel
            </Button>
            <jet-button
                class="ml-3"
                :class="{ 'opacity-25': modalNameVisibleForm.processing }"
                :disabled="modalNameVisibleForm.processing"
                @click="modalNameVisibleFormSubmit()"
            >
                <span>{{ modalNameVisible.is_masked ? "Unmask my ride" : "Mask my ride" }}</span>
            </jet-button>
        </template>
    </dialog-modal>
</template>
